import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  useInfiniteQuery,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type QueryClient,
} from "@tanstack/react-query";
import { TriangleAlert, FileText, X } from "lucide-react";
import {
  MAX_MESSAGE_ATTACHMENTS,
  PlayerAIProblem,
  describePlayerAIFailure,
  playerAIFailureCode,
  usePlayerAIClient,
  type AttachedDocumentDeletePreview,
  type ConversationAttachment,
  type ConversationAttachmentPage,
  type MyFile,
  type MyFilePage,
} from "@playbookai/player-ai-client";
import {
  TRANSIENT_DOCUMENT_RETRIEVAL_STATUSES,
  documentUploadDisabledReason,
  DOCUMENT_SUPPORT_SETUP_MESSAGE,
} from "@playbookai/api-clients";
import {
  Button,
  EmptyState,
  FileUpload,
  FormField,
  Modal,
  Progress,
  TruncatedText,
  toast,
  uploadExactObject,
  useRetainedValue,
} from "@playbookai/ui";
import {
  AttachmentRetrievalLine,
  AttachmentRowShell,
  ConversationFilesPopover,
  UPLOADING_ATTACHMENT_STATUSES,
  attachmentFailure,
  attachmentFilename,
} from "./ConversationFilesPopover.js";

const MAX_DOCUMENT_BYTES = 25 * 1024 * 1024;
const TRANSIENT_STATUSES = new Set(["creating", "uploading", "checking"]);
/**
 * PB-5380. One sentence for every place the per-message file limit stops a
 * person — the refusal, the disabled Add file, the disabled drop zone and the
 * disabled My Files Attach — so one rule reads as one rule.
 */
const ATTACHMENT_LIMIT_COPY = `Player AI can take up to ${MAX_MESSAGE_ATTACHMENTS} files in one message. Send this message first, or remove a file.`;

const attachmentQueryKey = (conversationId: string | null) => [
  "player-ai",
  "conversation-attachments",
  conversationId,
];
const myFilesQueryKey = (conversationId: string | null) => [
  "player-ai",
  "conversation-my-files",
  conversationId,
];

function attachmentPollInterval(successfulRefreshes: number): number {
  const backoffStep = Math.min(Math.floor(successfulRefreshes / 4), 4);
  return 1_500 * 2 ** backoffStep;
}

interface QueuedUpload {
  readonly id: string;
  readonly file: File;
  readonly conversationId: string;
  readonly slot: symbol;
  readonly status: "queued" | "uploading" | "error";
  readonly error?: string;
  readonly retryKey?: string;
}

interface UploadFailure {
  readonly error: string;
  readonly retryKey?: string;
}

const UPLOAD_CONCURRENCY = 3;

interface DeleteCandidate {
  readonly attachment: ConversationAttachment;
  readonly preview: AttachedDocumentDeletePreview;
}

function LoadMoreFiles(props: {
  readonly loading: boolean;
  readonly onLoadMore: () => void;
}): ReactNode {
  /* Kept as a centred Button: ProgressiveListFooter draws a top rule, a
     count and an outline md button, a look change no PR 3 row approves. */
  return (
    <div className="flex justify-center">
      <Button
        type="button"
        size="sm"
        variant="secondary"
        loading={props.loading}
        onClick={props.onLoadMore}
      >
        Load more files
      </Button>
    </div>
  );
}

export interface PlayerAIAttachmentSlots {
  readonly leadingSlot: ReactNode;
  readonly metadataSlot: ReactNode;
  /**
   * Attachment id → current display name, for the conversation the viewer is
   * already authorized to read. Read-only and derived from the same live query
   * as the cards: an unlinked or deleted attachment drops out on the next
   * refresh, so a caller can never render a stale name.
   */
  readonly displayNamesByAttachmentId: ReadonlyMap<string, string>;
  /** Files selected for the NEXT message and not yet sent with one. */
  readonly pendingAttachments: readonly ConversationAttachment[];
  readonly hasPendingUpload: boolean;
  /**
   * Drop exactly the ids a message actually carried. Anything the user added
   * while that send was in flight is not in `ids`, so it survives and stays
   * selected for the next message.
   */
  readonly consumePendingAttachments: (ids: readonly string[]) => void;
}

/**
 * Conversation-level file metadata for the shell-owned Player AI surface.
 * The next user Message may receive safe attachment ids and display names for
 * UI provenance; document contents still never enter messages, prompts, or AIS.
 */
export function usePlayerAIAttachments(input: {
  readonly conversationId: string | null;
  readonly canMutate: boolean;
  /**
   * Task 7 — the same fail-closed rule `sendMessage`, `startNext` and
   * `decideProposal` follow: a server 403 means this client's view of its own
   * access is stale, so ask for a fresh authoritative read rather than leaving a
   * live Add-file button over an authority that has already moved.
   *
   * Optional so a standalone mount behaves exactly as before.
   */
  readonly onAccessRefused?: (() => void) | undefined;
}): PlayerAIAttachmentSlots {
  const client = usePlayerAIClient();
  const queryClient = useQueryClient();
  const [progress, setProgress] = useState<Readonly<Record<string, number>>>(
    {},
  );
  const [pickerOpen, setPickerOpen] = useState(false);
  const [filesOpen, setFilesOpen] = useState(false);
  const [myFilesQuery, setMyFilesQuery] = useState("");
  const [busyId, setBusyId] = useState<string | null>(null);
  const [retryingAttachmentId, setRetryingAttachmentId] = useState<
    string | null
  >(null);
  const [error, setError] = useState<string | null>(null);
  const [pendingAttachmentIds, setPendingAttachmentIds] = useState<
    readonly string[]
  >([]);
  /*
   * PB-5380 — the per-message file limit, enforced before any request.
   *
   * A slot is taken by a file selected for the next message, or RESERVED by a
   * pick whose request has not answered yet. The reservation is made before
   * the first await and belongs to one conversation, so two clicks in one tick
   * cannot both take the last slot, and a late answer from a conversation the
   * person has left cannot free a slot in the one they are in now. The
   * selection lives in a ref as well as in state because a second click in the
   * same tick reads it before React re-renders.
   */
  const selectedIdsRef = useRef<readonly string[]>([]);
  const reservationsRef = useRef(new Map<symbol, string>());
  const [reservedCount, setReservedCount] = useState(0);
  /* Selected ids the list has shown. One that then leaves the list was removed
     elsewhere; the send would not carry it, so it gives its slot back. */
  const listedSelectionRef = useRef(new Set<string>());
  const [activeUploadCounts, setActiveUploadCounts] = useState<
    Readonly<Record<string, number>>
  >({});
  const [deleteCandidate, setDeleteCandidate] =
    useState<DeleteCandidate | null>(null);
  /* The confirm is hoisted onto `open` so it can animate out (ADR-0091), which
     means its body renders one frame after the candidate is already null.
     Retaining it keeps that frame naming the file it was about. */
  const shownDeleteCandidate = useRetainedValue(deleteCandidate);
  const transientPollBaseline = useRef<number | null>(null);
  const controllers = useRef(new Map<string, AbortController>());
  const activeRetryRef = useRef<symbol | null>(null);
  const activeConversationRef = useRef(input.conversationId);
  activeConversationRef.current = input.conversationId;
  const mountedRef = useRef(true);
  const canMutateRef = useRef(input.canMutate);
  canMutateRef.current = input.canMutate;
  const support = useQuery({
    queryKey: ["player-ai", "document-support", input.conversationId],
    queryFn: () => client.getDocumentSupport(input.conversationId!),
    enabled: input.conversationId !== null && input.canMutate,
    retry: false,
    staleTime: 30_000,
  });
  const uploadDisabledReason = documentUploadDisabledReason(
    support.data,
    support.isError,
  );
  const uploadDisabledRef = useRef(uploadDisabledReason);
  uploadDisabledRef.current = uploadDisabledReason;
  const supportReasonId = useId();
  const uploadQueue = useRef<QueuedUpload[]>([]);
  const runningUploads = useRef(0);
  const pumpUploadsRef = useRef<() => void>(() => undefined);
  const [uploadEntries, setUploadEntries] = useState<QueuedUpload[]>([]);
  const [rejectedUploads, setRejectedUploads] = useState<string[]>([]);
  const limitReasonId = useId();
  const retryLimitReasonId = useId();
  const commitSelection = useCallback(
    (next: (current: readonly string[]) => readonly string[]): void => {
      const updated = next(selectedIdsRef.current);
      if (updated === selectedIdsRef.current) return;
      selectedIdsRef.current = updated;
      setPendingAttachmentIds(updated);
    },
    [],
  );
  const syncReservedCount = useCallback((): void => {
    let reserved = 0;
    for (const owner of reservationsRef.current.values()) {
      if (owner === activeConversationRef.current) reserved += 1;
    }
    setReservedCount(reserved);
  }, []);
  /** Slots this conversation's next message has already used, right now. */
  const takenSlots = useCallback((conversationId: string): number => {
    let taken = selectedIdsRef.current.length;
    for (const owner of reservationsRef.current.values()) {
      if (owner === conversationId) taken += 1;
    }
    return taken;
  }, []);
  const reserveSlot = useCallback(
    (conversationId: string): symbol => {
      const slot = Symbol("attachment-slot");
      reservationsRef.current.set(slot, conversationId);
      syncReservedCount();
      return slot;
    },
    [syncReservedCount],
  );
  const releaseSlot = useCallback(
    (slot: symbol): void => {
      if (reservationsRef.current.delete(slot)) syncReservedCount();
    },
    [syncReservedCount],
  );
  const reportAccessRefusal = useCallback(
    (error: unknown, conversationId: string) => {
      if (
        activeConversationRef.current === conversationId &&
        isAccessRefusal(error)
      )
        input.onAccessRefused?.();
    },
    [input.onAccessRefused],
  );

  const attachmentKey = useMemo(
    () => attachmentQueryKey(input.conversationId),
    [input.conversationId],
  );
  const attachmentPages = useInfiniteQuery({
    queryKey: attachmentKey,
    queryFn: ({
      pageParam,
      signal,
    }: {
      pageParam: string | undefined;
      signal: AbortSignal;
    }) =>
      client.listAttachments(input.conversationId!, {
        ...(pageParam ? { cursor: pageParam } : {}),
        limit: 50,
        signal,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page) => page.next_cursor ?? undefined,
    enabled: input.conversationId !== null,
    refetchInterval: (query) => {
      const active = query.state.data?.pages.some((page) =>
        page.attachments?.some(
          (item) =>
            TRANSIENT_STATUSES.has(item.status) ||
            (item.retrieval != null &&
              TRANSIENT_DOCUMENT_RETRIEVAL_STATUSES.has(item.retrieval.status)),
        ),
      );
      if (!active) {
        transientPollBaseline.current = null;
        return false;
      }
      if (
        transientPollBaseline.current === null ||
        query.state.dataUpdateCount < transientPollBaseline.current
      ) {
        transientPollBaseline.current = query.state.dataUpdateCount;
      }
      return attachmentPollInterval(
        query.state.dataUpdateCount - transientPollBaseline.current,
      );
    },
  });
  const attachments = useMemo(
    () =>
      (attachmentPages.data?.pages ?? []).reduce<ConversationAttachment[]>(
        (current, page) => mergeAttachments(current, page.attachments ?? []),
        [],
      ),
    [attachmentPages.data?.pages],
  );

  useEffect(() => {
    if (attachmentPages.hasNextPage && !attachmentPages.isFetchingNextPage) {
      void attachmentPages.fetchNextPage();
    }
  }, [
    attachmentPages.fetchNextPage,
    attachmentPages.hasNextPage,
    attachmentPages.isFetchingNextPage,
  ]);

  const catalogue = useInfiniteQuery({
    queryKey: myFilesQueryKey(input.conversationId),
    queryFn: ({
      pageParam,
      signal,
    }: {
      pageParam: string | undefined;
      signal: AbortSignal;
    }) =>
      client.listMyFiles(input.conversationId!, {
        ...(pageParam ? { cursor: pageParam } : {}),
        limit: 25,
        signal,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page) => page.next_cursor ?? undefined,
    enabled: pickerOpen && input.conversationId !== null && input.canMutate,
    staleTime: 10_000,
  });
  const myFiles = useMemo(
    () =>
      (catalogue.data?.pages ?? []).reduce<MyFile[]>(
        (current, page) => mergeMyFiles(current, page.items ?? []),
        [],
      ),
    [catalogue.data?.pages],
  );

  const refresh = useCallback(
    (): Promise<void> =>
      queryClient
        .invalidateQueries({ queryKey: attachmentKey, exact: true })
        .then(() => undefined),
    [attachmentKey, queryClient],
  );

  useEffect(() => {
    for (const controller of controllers.current.values()) controller.abort();
    controllers.current.clear();
    activeRetryRef.current = null;
    transientPollBaseline.current = null;
    for (const job of uploadQueue.current.splice(0)) {
      reservationsRef.current.delete(job.slot);
    }
    setUploadEntries([]);
    setRejectedUploads([]);
    setProgress({});
    setPickerOpen(false);
    setFilesOpen(false);
    setMyFilesQuery("");
    setDeleteCandidate(null);
    setBusyId(null);
    setRetryingAttachmentId(null);
    setError(null);
    commitSelection(() => []);
    listedSelectionRef.current.clear();
    syncReservedCount();
  }, [commitSelection, input.conversationId, syncReservedCount]);

  useEffect(() => {
    mountedRef.current = true;
    const activeControllers = controllers.current;
    return () => {
      mountedRef.current = false;
      for (const controller of activeControllers.values()) controller.abort();
      activeControllers.clear();
      uploadQueue.current.length = 0;
      reservationsRef.current.clear();
    };
  }, []);

  const openPicker = useCallback((): void => {
    if (!input.canMutate || !input.conversationId) return;
    setMyFilesQuery("");
    setPickerOpen(true);
  }, [input.canMutate, input.conversationId]);

  const filteredMyFiles = useMemo(() => {
    const query = myFilesQuery.trim().toLocaleLowerCase();
    return query
      ? myFiles.filter((item) =>
          item.display_filename.toLocaleLowerCase().includes(query),
        )
      : myFiles;
  }, [myFiles, myFilesQuery]);

  const upload = useCallback(
    async (
      file: File,
      slot: symbol,
      conversationId: string,
      idempotencyKey: string,
    ): Promise<UploadFailure | null> => {
      setPickerOpen(false);
      setActiveUploadCounts((current) => ({
        ...current,
        [conversationId]: (current[conversationId] ?? 0) + 1,
      }));
      let attachmentId: string | null = null;
      let stage: "creating" | "uploading" | "completing" = "creating";
      try {
        if (uploadDisabledRef.current)
          throw new Error(uploadDisabledRef.current);
        const session = await client.createAttachmentUpload(conversationId, {
          idempotencyKey,
          displayFilename: file.name,
          ...(file.type ? { declaredMediaType: file.type } : {}),
          declaredSizeBytes: file.size,
        });
        attachmentId = session.attachment.id;
        if (
          !mountedRef.current ||
          activeConversationRef.current !== conversationId ||
          !canMutateRef.current
        ) {
          await client.abortAttachmentUpload(conversationId, attachmentId);
          return null;
        }
        if (session.display_filename_adjusted === true) {
          // Task 3 — the server removed characters it cannot store; the upload
          // carries on under the cleaned name and the file itself is untouched.
          toast("Filename adjusted to allow upload.", {
            dedupeKey: `player-ai-filename-adjusted:${session.attachment.id}`,
          });
        }
        // Listed BEFORE it is selected, as `reuse` does: a selected id is then
        // always one the list has shown, which is what the count relies on.
        await cacheAttachment(queryClient, conversationId, session.attachment);
        if (activeConversationRef.current === conversationId) {
          commitSelection((current) =>
            current.includes(session.attachment.id)
              ? current
              : [...current, session.attachment.id],
          );
        }
        releaseSlot(slot);
        if (
          !mountedRef.current ||
          activeConversationRef.current !== conversationId ||
          !canMutateRef.current
        ) {
          commitSelection((current) =>
            current.filter((value) => value !== attachmentId),
          );
          await client.abortAttachmentUpload(conversationId, attachmentId);
          return null;
        }
        setProgress((current) => ({ ...current, [session.attachment.id]: 0 }));
        const controller = new AbortController();
        controllers.current.set(session.attachment.id, controller);
        stage = "uploading";
        await uploadExactObject({
          url: session.upload.url,
          file,
          requiredHeaders: session.upload.required_headers,
          signal: controller.signal,
          onProgress: ({ percent }) => {
            if (activeConversationRef.current !== conversationId) return;
            setProgress((current) => ({
              ...current,
              [session.attachment.id]: percent,
            }));
          },
        });
        stage = "completing";
        const completed = await client.completeAttachmentUpload(
          conversationId,
          session.attachment.id,
        );
        if (activeConversationRef.current === conversationId) {
          await cacheAttachment(
            queryClient,
            conversationId,
            completed.attachment,
          );
        }
        return null;
      } catch (uploadError) {
        // Replay an unacknowledged creation with its original intent key. A
        // known upload gets a new key only after cancellation is confirmed.
        // A lost completion response may already have succeeded: do not abort
        // or offer another upload until the person checks Conversation files.
        let retryKey = attachmentId === null ? idempotencyKey : undefined;
        if (attachmentId && stage !== "completing") {
          try {
            await client.abortAttachmentUpload(conversationId, attachmentId);
            retryKey = crypto.randomUUID();
          } catch {
            // An uncertain cancellation is not proof that a second upload is safe.
          }
        }
        // A stale conversation refusal must not refresh the newly selected one.
        reportAccessRefusal(uploadError, conversationId);
        if (activeConversationRef.current === conversationId) {
          if (attachmentId && stage !== "completing") {
            commitSelection((current) =>
              current.filter((value) => value !== attachmentId),
            );
          }
          setError(attachmentError(uploadError, "write"));
        }
        return {
          error: attachmentError(uploadError, "write"),
          ...(retryKey ? { retryKey } : {}),
        };
      } finally {
        releaseSlot(slot);
        setActiveUploadCounts((current) => {
          const remaining = (current[conversationId] ?? 1) - 1;
          return remaining > 0
            ? { ...current, [conversationId]: remaining }
            : omitKey(current, conversationId);
        });
        if (attachmentId) {
          const completedAttachmentId = attachmentId;
          controllers.current.delete(completedAttachmentId);
          setProgress((current) => omitKey(current, completedAttachmentId));
        }
        if (activeConversationRef.current === conversationId) void refresh();
      }
    },
    [
      client,
      commitSelection,
      input.canMutate,
      input.conversationId,
      reportAccessRefusal,
      queryClient,
      refresh,
      releaseSlot,
    ],
  );

  const pumpUploads = useCallback((): void => {
    while (
      mountedRef.current &&
      runningUploads.current < UPLOAD_CONCURRENCY &&
      uploadQueue.current.length > 0
    ) {
      const job = uploadQueue.current.shift()!;
      if (activeConversationRef.current !== job.conversationId) {
        releaseSlot(job.slot);
        continue;
      }
      if (!canMutateRef.current) {
        releaseSlot(job.slot);
        setUploadEntries((current) =>
          current.map((item) =>
            item.id === job.id
              ? {
                  ...item,
                  status: "error",
                  error: "You can no longer add files to this conversation.",
                }
              : item,
          ),
        );
        continue;
      }
      runningUploads.current += 1;
      setUploadEntries((current) =>
        current.map((item) =>
          item.id === job.id ? { ...item, status: "uploading" } : item,
        ),
      );
      void upload(job.file, job.slot, job.conversationId, job.id)
        .then((failure) => {
          if (activeConversationRef.current === job.conversationId) {
            setUploadEntries((current) =>
              failure
                ? current.map((item) =>
                    item.id === job.id
                      ? {
                          ...item,
                          status: "error",
                          error: failure.error,
                          ...(failure.retryKey
                            ? { retryKey: failure.retryKey }
                            : {}),
                        }
                      : item,
                  )
                : current.filter((item) => item.id !== job.id),
            );
          }
        })
        .finally(() => {
          runningUploads.current -= 1;
          pumpUploadsRef.current();
        });
    }
  }, [releaseSlot, upload]);
  pumpUploadsRef.current = pumpUploads;

  const enqueueUploads = useCallback(
    (files: readonly File[], retryKey?: string): void => {
      const conversationId = input.conversationId;
      if (!conversationId) return;
      if (uploadDisabledReason) {
        setError(uploadDisabledReason);
        return;
      }
      if (!input.canMutate) {
        setError(
          "You can't add files to this conversation. Your selection wasn't uploaded.",
        );
        return;
      }
      const admitted: QueuedUpload[] = [];
      for (const file of files) {
        if (takenSlots(conversationId) >= MAX_MESSAGE_ATTACHMENTS) {
          setError(ATTACHMENT_LIMIT_COPY);
          setRejectedUploads((current) => [
            ...current,
            `${file.name}: ${ATTACHMENT_LIMIT_COPY}`,
          ]);
          continue;
        }
        admitted.push({
          id: retryKey ?? crypto.randomUUID(),
          file,
          conversationId,
          slot: reserveSlot(conversationId),
          status: "queued",
        });
      }
      uploadQueue.current.push(...admitted);
      setUploadEntries((current) => [...current, ...admitted]);
      pumpUploads();
    },
    [
      input.canMutate,
      input.conversationId,
      pumpUploads,
      reserveSlot,
      takenSlots,
      uploadDisabledReason,
    ],
  );

  const cancel = useCallback(
    async (attachmentId: string): Promise<void> => {
      const conversationId = input.conversationId;
      if (!conversationId || !input.canMutate) return;
      const controller = controllers.current.get(attachmentId);
      setBusyId(attachmentId);
      try {
        if (controller) controller.abort();
        await client.abortAttachmentUpload(conversationId, attachmentId);
        commitSelection((current) =>
          current.filter((value) => value !== attachmentId),
        );
        await refresh();
      } catch (requestError) {
        reportAccessRefusal(requestError, conversationId);
        // The server may have cancelled the upload before its response was
        // lost. Use uncertain-write guidance rather than a blind retry.
        setError(attachmentError(requestError, "write"));
      } finally {
        setBusyId(null);
      }
    },
    [
      client,
      commitSelection,
      reportAccessRefusal,
      input.canMutate,
      input.conversationId,
      refresh,
    ],
  );

  const reuse = useCallback(
    async (documentId: string): Promise<void> => {
      const conversationId = input.conversationId;
      if (!conversationId || !input.canMutate) return;
      // Refused before any request, with the picker left open.
      if (takenSlots(conversationId) >= MAX_MESSAGE_ATTACHMENTS) {
        setError(ATTACHMENT_LIMIT_COPY);
        return;
      }
      const slot = reserveSlot(conversationId);
      setBusyId(documentId);
      try {
        const result = await client.reuseAttachment(
          conversationId,
          documentId,
          crypto.randomUUID(),
        );
        if (activeConversationRef.current !== conversationId) return;
        await cacheAttachment(queryClient, conversationId, result.attachment);
        commitSelection((current) =>
          current.includes(result.attachment.id)
            ? current
            : [...current, result.attachment.id],
        );
        releaseSlot(slot);
        setPickerOpen(false);
        setError(null);
      } catch (requestError) {
        reportAccessRefusal(requestError, conversationId);
        setError(attachmentError(requestError, "write"));
      } finally {
        releaseSlot(slot);
        setBusyId(null);
      }
    },
    [
      client,
      commitSelection,
      reportAccessRefusal,
      input.canMutate,
      input.conversationId,
      queryClient,
      releaseSlot,
      reserveSlot,
      takenSlots,
    ],
  );

  const download = useCallback(
    async (attachmentId: string): Promise<void> => {
      const conversationId = input.conversationId;
      if (!conversationId) return;
      setBusyId(attachmentId);
      try {
        const session = await client.createAttachmentDownload(
          conversationId,
          attachmentId,
        );
        const anchor = document.createElement("a");
        anchor.href = session.url;
        anchor.target = "_self";
        document.body.append(anchor);
        try {
          anchor.click();
        } finally {
          anchor.remove();
        }
      } catch (requestError) {
        reportAccessRefusal(requestError, conversationId);
        setError(attachmentError(requestError));
      } finally {
        setBusyId(null);
      }
    },
    [client, input.conversationId, reportAccessRefusal],
  );

  const unlink = useCallback(
    async (attachmentId: string): Promise<void> => {
      const conversationId = input.conversationId;
      if (!conversationId || !input.canMutate) return;
      setBusyId(attachmentId);
      try {
        await client.unlinkAttachment(conversationId, attachmentId);
        commitSelection((current) =>
          current.filter((value) => value !== attachmentId),
        );
        await removeCachedAttachment(queryClient, conversationId, attachmentId);
        setError(null);
      } catch (requestError) {
        reportAccessRefusal(requestError, conversationId);
        setError(attachmentError(requestError, "write"));
      } finally {
        setBusyId(null);
      }
    },
    [
      client,
      commitSelection,
      reportAccessRefusal,
      input.canMutate,
      input.conversationId,
      queryClient,
    ],
  );

  const retryRetrieval = useCallback(
    async (attachmentId: string): Promise<void> => {
      const conversationId = input.conversationId;
      if (!conversationId || !input.canMutate) return;
      if (activeRetryRef.current !== null) return;
      const retryToken = Symbol("attachment-retrieval-retry");
      activeRetryRef.current = retryToken;
      if (activeConversationRef.current === conversationId) {
        setBusyId(attachmentId);
        setRetryingAttachmentId(attachmentId);
      }
      try {
        const result = await client.retryAttachmentRetrieval(
          conversationId,
          attachmentId,
          crypto.randomUUID(),
        );
        if (
          activeRetryRef.current !== retryToken ||
          activeConversationRef.current !== conversationId
        ) {
          return;
        }
        await cacheAttachment(queryClient, conversationId, result.attachment);
        if (
          activeRetryRef.current === retryToken &&
          activeConversationRef.current === conversationId
        ) {
          setError(null);
        }
      } catch (requestError) {
        if (
          activeRetryRef.current === retryToken &&
          activeConversationRef.current === conversationId
        ) {
          reportAccessRefusal(requestError, conversationId);
          setError(attachmentError(requestError, "write"));
        }
      } finally {
        if (activeRetryRef.current === retryToken) {
          activeRetryRef.current = null;
          if (activeConversationRef.current === conversationId) {
            setRetryingAttachmentId(null);
            setBusyId((current) => (current === attachmentId ? null : current));
          }
        }
      }
    },
    [
      client,
      reportAccessRefusal,
      input.canMutate,
      input.conversationId,
      queryClient,
    ],
  );

  const previewDelete = useCallback(
    async (attachment: ConversationAttachment): Promise<void> => {
      const conversationId = input.conversationId;
      if (!conversationId || !input.canMutate) return;
      setBusyId(attachment.id);
      try {
        const preview = await client.previewAttachmentDocumentDelete(
          conversationId,
          attachment.id,
        );
        setDeleteCandidate({ attachment, preview });
        setError(null);
      } catch (requestError) {
        reportAccessRefusal(requestError, conversationId);
        setError(attachmentError(requestError));
      } finally {
        setBusyId(null);
      }
    },
    [client, reportAccessRefusal, input.canMutate, input.conversationId],
  );

  const confirmDelete = useCallback(async (): Promise<void> => {
    const conversationId = input.conversationId;
    const candidate = deleteCandidate;
    if (!conversationId || !candidate || !input.canMutate) return;
    setBusyId(candidate.attachment.id);
    try {
      const result = await client.deleteAttachmentDocument(
        conversationId,
        candidate.attachment.id,
        candidate.preview.delete_version,
      );
      commitSelection((current) =>
        current.filter((value) => value !== candidate.attachment.id),
      );
      await cacheAttachment(queryClient, conversationId, result.attachment);
      setDeleteCandidate(null);
      await removeCachedMyFile(
        queryClient,
        conversationId,
        candidate.preview.document_id,
      );
      setError(null);
    } catch (requestError) {
      reportAccessRefusal(requestError, conversationId);
      setDeleteCandidate(null);
      setError(attachmentError(requestError, "write"));
    } finally {
      setBusyId(null);
    }
  }, [
    client,
    commitSelection,
    deleteCandidate,
    reportAccessRefusal,
    input.canMutate,
    input.conversationId,
    queryClient,
  ]);

  const displayedError =
    error ??
    (attachmentPages.error ? attachmentError(attachmentPages.error) : null) ??
    (catalogue.error ? attachmentError(catalogue.error) : null);

  useEffect(() => {
    const listed = new Set(attachments.map((attachment) => attachment.id));
    const left = pendingAttachmentIds.filter((attachmentId) => {
      if (listed.has(attachmentId)) {
        listedSelectionRef.current.add(attachmentId);
        return false;
      }
      return listedSelectionRef.current.has(attachmentId);
    });
    if (left.length === 0) return;
    for (const attachmentId of left) {
      listedSelectionRef.current.delete(attachmentId);
    }
    commitSelection((current) =>
      current.filter((attachmentId) => !left.includes(attachmentId)),
    );
  }, [attachments, commitSelection, pendingAttachmentIds]);

  /* What this message would carry, plus picks still uploading. */
  const takenCount = pendingAttachmentIds.length + reservedCount;
  const atAttachmentLimit = takenCount >= MAX_MESSAGE_ATTACHMENTS;
  useEffect(() => {
    if (error === ATTACHMENT_LIMIT_COPY && !atAttachmentLimit) setError(null);
  }, [atAttachmentLimit, error]);

  const countText =
    takenCount > 0
      ? `${takenCount} of ${MAX_MESSAGE_ATTACHMENTS} files for this message`
      : "";
  const leadingSlot = useMemo<ReactNode>(
    () => (
      <>
        {/* Always mounted, so the FIRST change (0 → 1) is announced too: a
            live region inserted together with its text is not reliably read.
            The visible line in the metadata slot is hidden from assistive
            technology, so the count is read once. */}
        <span className="sr-only" aria-live="polite" aria-atomic="true">
          {countText}
        </span>
        <ConversationFilesPopover
          attachments={attachments}
          open={filesOpen}
          onOpenChange={setFilesOpen}
          hasConversation={input.conversationId !== null}
          canMutate={input.canMutate}
          isLoading={attachmentPages.isLoading}
          error={displayedError}
          busyId={busyId}
          retryingAttachmentId={retryingAttachmentId}
          addFileDisabledReason={
            atAttachmentLimit ? ATTACHMENT_LIMIT_COPY : null
          }
          onAddFile={openPicker}
          onCancelUpload={(attachmentId) => void cancel(attachmentId)}
          onDownload={(attachmentId) => void download(attachmentId)}
          onRetryRetrieval={(attachmentId) => void retryRetrieval(attachmentId)}
          onRemove={(attachmentId) => void unlink(attachmentId)}
          onDeleteEverywhere={(attachment) => void previewDelete(attachment)}
        />
        {/* The body reads the catalogue and the upload state, never the flag,
            so it is already null-safe on the closing frame. */}
        <Modal
          open={pickerOpen}
          onClose={() => setPickerOpen(false)}
          size="md"
          title="Attach a file"
        >
          {/* Task 7 — a refusal has to appear WHERE the person is looking.
                The popover's copy of this message is behind a closed popover
                while the picker is open, so a selection that could not be
                uploaded read as nothing happening at all. */}
          {/* Kept hand-built: Alert paints the full soft fill, a glyph and
              px-3 py-2.5, a look change no PR 3 row approves. */}
          {displayedError ? (
            <p
              role="alert"
              id={
                displayedError === ATTACHMENT_LIMIT_COPY
                  ? limitReasonId
                  : undefined
              }
              className="mb-3 rounded-md border border-pb-red/40 bg-pb-red-soft px-2 py-1 text-dense-1 text-pb-text-secondary"
            >
              {displayedError}
            </p>
          ) : null}
          {/* A disabled control leaves the tab order, so its reason is visible
              text that each disabled control names as its description. */}
          {atAttachmentLimit && displayedError !== ATTACHMENT_LIMIT_COPY ? (
            <p
              id={limitReasonId}
              className="mb-3 text-dense-1 text-pb-text-muted"
            >
              {ATTACHMENT_LIMIT_COPY}
            </p>
          ) : null}
          {uploadDisabledReason ? (
            <p
              id={supportReasonId}
              role="status"
              className="mb-3 text-dense-1 text-pb-text-muted"
            >
              {uploadDisabledReason}
            </p>
          ) : null}
          {support.isError ? (
            <EmptyState
              variant="error"
              size="sm"
              title="Couldn't check document support"
              action={
                <Button size="sm" onClick={() => void support.refetch()}>
                  Retry
                </Button>
              }
            />
          ) : null}
          <FormField>
            <FileUpload
              accept=".pdf,.docx,.xlsx,.txt,.md"
              maxSize={MAX_DOCUMENT_BYTES}
              multiple
              maxFiles={Math.max(0, MAX_MESSAGE_ATTACHMENTS - takenCount)}
              value={[]}
              disabled={atAttachmentLimit || uploadDisabledReason !== null}
              aria-describedby={
                uploadDisabledReason
                  ? supportReasonId
                  : atAttachmentLimit
                    ? limitReasonId
                    : undefined
              }
              onReject={(rejections) => {
                setRejectedUploads((current) => [
                  ...current,
                  ...rejections.map((rejection) =>
                    rejection.reason === "count"
                      ? `${rejection.file.name}: ${ATTACHMENT_LIMIT_COPY}`
                      : rejection.message,
                  ),
                ]);
              }}
              onChange={(files) => {
                enqueueUploads(files.map((picked) => picked.file));
              }}
              hint={`PDF, DOCX, XLSX, TXT, or MD, up to 25 MB each. Up to ${MAX_MESSAGE_ATTACHMENTS} files per message. Upload begins when selected.`}
            />
          </FormField>
          <div className="mt-5 border-t border-pb-border pt-4">
            <h3 className="text-sm font-medium text-pb-text">My Files</h3>
            <p className="mb-2 text-dense-1 text-pb-text-muted">
              Reuse a ready file you uploaded earlier.
            </p>
            <label className="mb-2 block text-dense-1 text-pb-text-muted">
              <span className="sr-only">Search My Files</span>
              {/* eslint-disable-next-line ds/no-hand-built -- kept: Input md is a 36px px-3 field (today about 34px, px-2.5) and sm a 28px text-xs one; no PR 3 look row approves either. */}
              <input
                type="search"
                value={myFilesQuery}
                onChange={(event) => setMyFilesQuery(event.currentTarget.value)}
                placeholder="Search My Files…"
                className="w-full rounded-md border border-pb-control-border bg-pb-card px-2.5 py-1.5 text-sm text-pb-text outline-none focus:border-pb-teal/60"
              />
            </label>
            {/* Kept hand-built: a Spinner beside "Loading files…" is not on
                a PR 3 look row, EmptyState size="sm" drops the dashed frames
                below, and ListItem is one button where each row holds its own
                Attach button. */}
            {catalogue.isLoading ? (
              <p className="text-dense-1 text-pb-text-muted">Loading files…</p>
            ) : myFiles.length === 0 ? (
              <p className="rounded border border-dashed border-pb-border p-3 text-dense-1 text-pb-text-muted">
                No ready files yet.
              </p>
            ) : filteredMyFiles.length === 0 ? (
              <div className="space-y-2">
                <p className="rounded border border-dashed border-pb-border p-3 text-dense-1 text-pb-text-muted">
                  No matching files.
                </p>
                {catalogue.hasNextPage ? (
                  <LoadMoreFiles
                    loading={catalogue.isFetchingNextPage}
                    onLoadMore={() => void catalogue.fetchNextPage()}
                  />
                ) : null}
              </div>
            ) : (
              <div className="space-y-2">
                <ul className="max-h-52 space-y-1 overflow-y-auto">
                  {filteredMyFiles.map((item) => (
                    <li
                      key={item.document_id}
                      className="flex items-center gap-2 rounded border border-pb-border p-2"
                    >
                      <FileText
                        className="h-4 w-4 shrink-0 text-pb-text-muted"
                        aria-hidden
                      />
                      <div className="min-w-0 flex-1">
                        {/* A clipped name shows in the design-system Tooltip
                            (d1), not a native title. */}
                        <div className="text-sm text-pb-text">
                          <TruncatedText>{item.display_filename}</TruncatedText>
                        </div>
                        {item.verified_size_bytes !== null ? (
                          <div className="text-dense-1 text-pb-text-muted">
                            {formatBytes(item.verified_size_bytes)}
                          </div>
                        ) : null}
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        loading={busyId === item.document_id}
                        disabled={atAttachmentLimit}
                        aria-describedby={
                          atAttachmentLimit ? limitReasonId : undefined
                        }
                        onClick={() => void reuse(item.document_id)}
                      >
                        Attach
                      </Button>
                    </li>
                  ))}
                </ul>
                {catalogue.hasNextPage ? (
                  <LoadMoreFiles
                    loading={catalogue.isFetchingNextPage}
                    onLoadMore={() => void catalogue.fetchNextPage()}
                  />
                ) : null}
              </div>
            )}
          </div>
        </Modal>
        {shownDeleteCandidate ? (
          <Modal
            open={deleteCandidate !== null}
            onClose={() => setDeleteCandidate(null)}
            size="sm"
            title="Delete this file everywhere?"
            footer={
              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setDeleteCandidate(null)}
                >
                  Keep file
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  loading={busyId === shownDeleteCandidate.attachment.id}
                  onClick={() => void confirmDelete()}
                >
                  Delete everywhere
                </Button>
              </div>
            }
          >
            <p className="text-sm text-pb-text-secondary">
              This file is linked in{" "}
              {shownDeleteCandidate.preview.active_link_count}{" "}
              {shownDeleteCandidate.preview.active_link_count === 1
                ? "location"
                : "locations"}
              . Deleting it will make it unavailable in every existing artifact.
              This cannot be undone.
            </p>
          </Modal>
        ) : null}
      </>
    ),
    [
      atAttachmentLimit,
      uploadDisabledReason,
      supportReasonId,
      support.isError,
      support.refetch,
      attachments,
      attachmentPages.isLoading,
      busyId,
      countText,
      cancel,
      confirmDelete,
      deleteCandidate,
      displayedError,
      download,
      filesOpen,
      input.canMutate,
      input.conversationId,
      limitReasonId,
      filteredMyFiles,
      catalogue.fetchNextPage,
      catalogue.hasNextPage,
      catalogue.isFetchingNextPage,
      catalogue.isLoading,
      myFiles,
      myFilesQuery,
      openPicker,
      pickerOpen,
      previewDelete,
      retryRetrieval,
      retryingAttachmentId,
      reuse,
      shownDeleteCandidate,
      unlink,
      enqueueUploads,
      takenCount,
    ],
  );

  const pendingAttachments = useMemo(
    () =>
      pendingAttachmentIds.flatMap((attachmentId) => {
        const attachment = attachments.find((item) => item.id === attachmentId);
        return attachment ? [attachment] : [];
      }),
    [attachments, pendingAttachmentIds],
  );
  /* Uploading and checking hold the send back, because those bytes are not on
     the server yet. Retrieval — Azure indexing — deliberately does not: a file
     that is still being indexed, or whose indexing failed outright, is already
     attachable and downloadable. */
  const hasPendingUpload =
    reservedCount > 0 ||
    (input.conversationId !== null &&
      (activeUploadCounts[input.conversationId] ?? 0) > 0) ||
    pendingAttachments.some(
      (attachment) =>
        TRANSIENT_STATUSES.has(attachment.status) ||
        progress[attachment.id] != null,
    );
  const consumePendingAttachments = useCallback(
    (ids: readonly string[]) => {
      if (ids.length === 0) return;
      const consumed = new Set(ids);
      commitSelection((current) =>
        current.some((value) => consumed.has(value))
          ? current.filter((value) => !consumed.has(value))
          : current,
      );
    },
    [commitSelection],
  );

  /**
   * The composer strip is the NEXT MESSAGE's selection and nothing else, so it
   * empties itself as soon as that message sends. Files stay linked to the
   * conversation either way — the popover is where they live afterwards.
   */
  const metadataSlot = useMemo<ReactNode>(() => {
    if (
      takenCount === 0 &&
      !displayedError &&
      uploadEntries.length === 0 &&
      rejectedUploads.length === 0
    )
      return null;
    return (
      <div className="space-y-1.5" aria-live="polite">
        {/* Kept hand-built: Alert is a gap-3, px-3 py-2.5 box with a 40%
            border and a 16px glyph, a look change no PR 3 row approves. */}
        {displayedError ? (
          <div
            role="alert"
            className="flex items-start gap-1.5 rounded-md border border-pb-red/30 bg-pb-red-soft px-2 py-1.5 text-dense-1 text-pb-red-text"
          >
            <TriangleAlert
              className="mt-0.5 h-3.5 w-3.5 shrink-0"
              aria-hidden
            />
            <span>{displayedError}</span>
          </div>
        ) : null}
        {takenCount > 0 ? (
          <p className="text-dense-2 text-pb-text-muted" aria-hidden="true">
            {countText}
          </p>
        ) : null}
        {rejectedUploads.length > 0 ? (
          <div role="alert" className="text-dense-1 text-pb-red-text">
            <ul aria-label="Rejected files">
              {rejectedUploads.map((message, index) => (
                <li key={`${index}:${message}`}>{message}</li>
              ))}
            </ul>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setRejectedUploads([])}
            >
              Dismiss rejected files
            </Button>
          </div>
        ) : null}
        {atAttachmentLimit &&
        uploadEntries.some((item) => item.status === "error") ? (
          <p
            id={retryLimitReasonId}
            className="text-dense-1 text-pb-text-muted"
          >
            {ATTACHMENT_LIMIT_COPY}
          </p>
        ) : null}
        {uploadEntries.length > 0 ? (
          <ul aria-label="Upload queue" className="space-y-1">
            {uploadEntries
              .filter((item) => item.status !== "uploading")
              .map((item) => (
                <li
                  key={item.id}
                  className="flex items-center gap-2 text-dense-1 text-pb-text-secondary"
                >
                  <TruncatedText>{item.file.name}</TruncatedText>
                  <span>
                    {item.status === "error"
                      ? item.error
                      : "Waiting to upload…"}
                  </span>
                  {item.status === "error" &&
                  item.retryKey &&
                  input.canMutate ? (
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={atAttachmentLimit}
                      aria-describedby={
                        atAttachmentLimit ? retryLimitReasonId : undefined
                      }
                      onClick={() => {
                        if (
                          takenSlots(item.conversationId) >=
                          MAX_MESSAGE_ATTACHMENTS
                        )
                          return;
                        setError(null);
                        setUploadEntries((current) =>
                          current.filter((entry) => entry.id !== item.id),
                        );
                        enqueueUploads([item.file], item.retryKey);
                      }}
                    >
                      Retry upload: {item.file.name}
                    </Button>
                  ) : null}
                  {item.status === "error" ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      aria-label={`Dismiss failed upload: ${item.file.name}`}
                      onClick={() =>
                        setUploadEntries((current) =>
                          current.filter((entry) => entry.id !== item.id),
                        )
                      }
                    >
                      Dismiss
                    </Button>
                  ) : null}
                </li>
              ))}
          </ul>
        ) : null}
        {pendingAttachments.length > 0 ? (
          <ul
            aria-label="Files for the next message"
            className="max-h-40 space-y-1 overflow-y-auto"
          >
            {pendingAttachments.map((attachment) => {
              const filename = attachmentFilename(attachment);
              const uploading = UPLOADING_ATTACHMENT_STATUSES.has(
                attachment.status,
              );
              const percent = progress[attachment.id] ?? 0;
              const disabled =
                busyId === attachment.id ||
                retryingAttachmentId === attachment.id;
              return (
                <AttachmentRowShell
                  key={attachment.id}
                  attachment={attachment}
                  className="bg-pb-card"
                  actions={
                    input.canMutate ? (
                      // eslint-disable-next-line ds/no-hand-built -- kept: Button ghost iconOnly xs is a 24px box painting the glyph text-pb-text-secondary (today a 22px muted box, dimmed to 40%); no PR 3 look row approves that.
                      <button
                        type="button"
                        aria-label={
                          uploading
                            ? `Cancel upload: ${filename}`
                            : `Remove from this message: ${filename}`
                        }
                        onClick={() =>
                          uploading
                            ? void cancel(attachment.id)
                            : consumePendingAttachments([attachment.id])
                        }
                        disabled={disabled}
                        className="rounded-md p-1 text-pb-text-muted transition-colors hover:pb-overlay-paint hover:text-pb-text active:pb-overlay-paint-strong focus-ring disabled:opacity-40"
                      >
                        <X className="h-3.5 w-3.5" aria-hidden />
                      </button>
                    ) : null
                  }
                >
                  {uploading ? (
                    <div className="mt-1 space-y-1">
                      <span className="text-dense-2 text-pb-text-muted">
                        Uploading{percent > 0 ? ` ${percent}%` : "…"}
                      </span>
                      <Progress
                        value={percent}
                        aria-label={`Upload progress for ${filename}`}
                      />
                    </div>
                  ) : attachment.status === "checking" ? (
                    <p className="mt-0.5 text-dense-2 text-pb-text-muted">
                      Checking file…
                    </p>
                  ) : attachment.status === "ready" ? (
                    /* Retry lives in the popover, where the file's whole
                       lifecycle is on screen; the composer only reports. */
                    <AttachmentRetrievalLine
                      attachment={attachment}
                      canRetry={false}
                      retryDisabled
                      retrying={false}
                      onRetry={() => undefined}
                    />
                  ) : (
                    /* Kept as today's red line: Alert variant="inline" is
                       11px with a glyph, a look change no PR 3 row approves. */
                    <p className="mt-0.5 text-dense-2 text-pb-red-text">
                      {attachmentFailure(attachment)}
                    </p>
                  )}
                </AttachmentRowShell>
              );
            })}
          </ul>
        ) : null}
      </div>
    );
  }, [
    rejectedUploads,
    atAttachmentLimit,
    limitReasonId,
    takenSlots,
    uploadEntries,
    enqueueUploads,
    busyId,
    cancel,
    consumePendingAttachments,
    displayedError,
    input.canMutate,
    pendingAttachments,
    progress,
    retryingAttachmentId,
    countText,
    takenCount,
  ]);

  const displayNamesByAttachmentId = useMemo<ReadonlyMap<string, string>>(
    () =>
      new Map(
        attachments.flatMap((attachment) =>
          attachment.display_filename
            ? [[attachment.id, attachment.display_filename] as const]
            : [],
        ),
      ),
    [attachments],
  );

  return {
    leadingSlot,
    metadataSlot,
    displayNamesByAttachmentId,
    pendingAttachments,
    hasPendingUpload,
    consumePendingAttachments,
  };
}

function upsertAttachment(
  values: ConversationAttachment[],
  candidate: ConversationAttachment,
): ConversationAttachment[] {
  return [candidate, ...values.filter((item) => item.id !== candidate.id)].sort(
    (left, right) => Date.parse(right.created_at) - Date.parse(left.created_at),
  );
}

function mergeAttachments(
  current: ConversationAttachment[],
  incoming: ConversationAttachment[],
): ConversationAttachment[] {
  const byId = new Map(current.map((item) => [item.id, item]));
  for (const item of incoming) byId.set(item.id, item);
  return [...byId.values()].sort(
    (left, right) => Date.parse(right.created_at) - Date.parse(left.created_at),
  );
}

function cacheAttachment(
  queryClient: QueryClient,
  conversationId: string,
  candidate: ConversationAttachment,
): Promise<void> {
  const queryKey = attachmentQueryKey(conversationId);
  return queryClient.cancelQueries({ queryKey, exact: true }).then(() => {
    queryClient.setQueryData<InfiniteData<ConversationAttachmentPage>>(
      queryKey,
      (current) => upsertAttachmentPages(current, candidate),
    );
  });
}

function removeCachedAttachment(
  queryClient: QueryClient,
  conversationId: string,
  attachmentId: string,
): Promise<void> {
  const queryKey = attachmentQueryKey(conversationId);
  return queryClient.cancelQueries({ queryKey, exact: true }).then(() => {
    queryClient.setQueryData<InfiniteData<ConversationAttachmentPage>>(
      queryKey,
      (current) =>
        current
          ? {
              ...current,
              pages: current.pages.map((page) => ({
                ...page,
                attachments: page.attachments.filter(
                  (item) => item.id !== attachmentId,
                ),
              })),
            }
          : current,
    );
  });
}

function removeCachedMyFile(
  queryClient: QueryClient,
  conversationId: string,
  documentId: string,
): Promise<void> {
  const queryKey = myFilesQueryKey(conversationId);
  return queryClient.cancelQueries({ queryKey, exact: true }).then(() => {
    queryClient.setQueryData<InfiniteData<MyFilePage>>(queryKey, (current) =>
      current
        ? {
            ...current,
            pages: current.pages.map((page) => ({
              ...page,
              items: page.items.filter(
                (item) => item.document_id !== documentId,
              ),
            })),
          }
        : current,
    );
  });
}

function upsertAttachmentPages(
  current: InfiniteData<ConversationAttachmentPage> | undefined,
  candidate: ConversationAttachment,
): InfiniteData<ConversationAttachmentPage> {
  if (!current || current.pages.length === 0) {
    return {
      pages: [{ attachments: [candidate], next_cursor: null }],
      pageParams: [undefined],
    };
  }
  const alreadyPresent = current.pages.some((page) =>
    page.attachments.some((item) => item.id === candidate.id),
  );
  return {
    ...current,
    pages: current.pages.map((page, index) => {
      const pageContainsCandidate = page.attachments.some(
        (item) => item.id === candidate.id,
      );
      return {
        ...page,
        attachments:
          pageContainsCandidate || (!alreadyPresent && index === 0)
            ? upsertAttachment(page.attachments, candidate)
            : page.attachments,
      };
    }),
  };
}

function omitKey(
  values: Readonly<Record<string, number>>,
  key: string,
): Readonly<Record<string, number>> {
  const next = { ...values };
  delete next[key];
  return next;
}

function mergeMyFiles(current: MyFile[], incoming: MyFile[]): MyFile[] {
  const byId = new Map(current.map((item) => [item.document_id, item]));
  for (const item of incoming) byId.set(item.document_id, item);
  return [...byId.values()];
}

/**
 * A server refusal of the CALLER's authority, as opposed to a refusal of the
 * file. 404 counts: the conversation reads as absent to somebody who may no
 * longer see it, which is the same signal `refreshConversation` treats as a
 * confirmed denial.
 */
function isAccessRefusal(error: unknown): boolean {
  return (
    error instanceof PlayerAIProblem &&
    [403, 404].includes(error.problem.status ?? 0)
  );
}

/**
 * @param source Whether the failed operation CHANGED anything. A failed read
 * left the conversation's files untouched and can simply be asked for again; an
 * upload, an unlink or a delete may have landed before the answer was lost, and
 * the fallback copy must not tell the reader to just try it again.
 */
function attachmentError(
  error: unknown,
  source: "read" | "write" = "read",
): string {
  if (error instanceof DOMException && error.name === "AbortError") {
    return "Upload cancelled.";
  }
  if (error instanceof PlayerAIProblem) {
    switch (error.problem.code) {
      case "DOCUMENT_SUPPORT_SETUP_REQUIRED":
        return DOCUMENT_SUPPORT_SETUP_MESSAGE;
      case "FILE_TOO_LARGE":
        return "This file is larger than 25 MB.";
      case "UNSUPPORTED_TYPE":
      case "UNSUPPORTED_FILE_TYPE":
        return "Use PDF, DOCX, XLSX, TXT, or MD.";
      case "DELETE_VERSION_CONFLICT":
        return "The file links changed. Review the delete warning again.";
      case "DEPENDENCY_UNAVAILABLE":
        return "File storage is temporarily unavailable. Try again.";
      // Task 3 — nothing usable was left of the name; retrying the same file
      // cannot help, so ask for the one fix that will.
      case "ATTACHMENT_FILENAME_UNUSABLE":
        return "This file name can't be used. Rename the file, then add it again.";
    }
  }
  /*
   * PB-5276 — the fallback, for everything the four file-specific messages
   * above do not name.
   *
   * It used to be `error.problem.detail` for a problem envelope and
   * `error.message` for anything else. The first put the server's own sentence
   * on screen; the second put a `fetch` TypeError's browser-specific wording
   * there, and — once a non-problem upstream response stopped carrying its body
   * — an EMPTY string, which is a failure notice that says nothing at all.
   *
   * The four messages above are kept: they are domain-specific, actionable and
   * better than any family sentence. This only replaces the raw tail.
   */
  const described = describePlayerAIFailure({
    source,
    ...playerAIFailureCode(error),
  });
  return `${described.title} ${described.message}`;
}

function formatBytes(value: number): string {
  if (value < 1_024) return `${value} B`;
  if (value < 1_024 * 1_024) return `${(value / 1_024).toFixed(1)} KB`;
  return `${(value / (1_024 * 1_024)).toFixed(1)} MB`;
}
